import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnInit,
    signal,
} from '@angular/core';
import {
    MultiRegistrationConfig,
    MultiregistrationController,
    UIParticipant,
    UIParticipantStatus,
    UIState,
} from '@azavista/participants/dist/event-app/multiregistration/multiregistration-controller';
import { IEventSettings, IField, IObjectWithId, ISchema } from '../interfaces';
import { CommonModule } from '@angular/common';
import { delay, getMapByGetter } from '../utils.service';
import { ApplyFunctionPipe } from '../apply-function.pipe';
import { SharedService } from '../shared.service';
import { TRANSLATION_MAP } from 'src/app/enums';
import { TranslateModule } from '@ngx-translate/core';
import { AppDomService } from 'src/app/app-dom.service';
import { firstValueFrom } from 'rxjs';
import { IRowActionGetter } from 'src/app/table/interfaces';
import {
    findParticipantById,
    getFieldEditableByStatus,
    getFieldWithParticipantStatus,
    getIsRequireTicket,
    getIsRequireUniqueEmail,
    getParentRecursively,
    getStateHasError,
    getUniqueEmailValidation,
} from './utils';
import { ValidatorService } from '../validator.service';
import {
    MULTI_REGISTRATION_ATTRIBUTE_NAMES,
    TableRowObject,
    DraftSubParticipantRecord,
} from './types';
import { MultiRegistrationFormTableComponent } from './multi-registration-form.table.component';
import { MultiRegistrationFormService } from './multi-registration-form.service';
import { AttributeName as GlobalAttributeName } from 'src/app/interfaces';

@Component({
    selector: 'aza-rws-multi-registration-form',
    imports: [
        CommonModule,
        TranslateModule,
        MultiRegistrationFormTableComponent,
        ApplyFunctionPipe,
    ],
    templateUrl: `./multi-registration-form.component.html`,
    styleUrls: ['./multi-registration-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiRegistrationFormComponent implements AfterViewInit, OnInit {
    @Input({
        required: true,
    })
    controller: MultiregistrationController;

    private _state: UIState;

    @Input({
        required: true,
    })
    public set state(value: UIState) {
        this._state = value;
    }

    public get state(): UIState {
        return this._state;
    }

    @Input({
        required: true,
    })
    allParticipantFields?: IField[];

    private _eventParticipant?: IObjectWithId;
    public get eventParticipant(): IObjectWithId {
        return this._eventParticipant;
    }
    @Input({
        required: true,
    })
    public set eventParticipant(value: IObjectWithId) {
        this._eventParticipant = value;
        this.controller.updateParticipant(this.state.main_participant.id, this.eventParticipant);

        delay(0).then(() => {
            const emailFieldId = this.subParticipantEmailField?.id;
            if (getIsRequireUniqueEmail(this.eventSettings) && emailFieldId) {
                const mainParticipantId = this.state.main_participant.id;
                const validationResult = this.getUniqueEmailValidation(
                    this.state, emailFieldId, value[emailFieldId], mainParticipantId
                );
                if (!validationResult.isValid) {
                    this.controller.setSubParticipantError(mainParticipantId, 'EMAIL_ALREADY_EXISTS');
                }
            }
        });
    }

    @Input({
        required: true,
    })
    config: MultiRegistrationConfig;

    @Input({
        required: true,
    })
    eventSettings?: IEventSettings;

    subParticipantEmailField: IField;
    subParticipantFields: IField[] = [];

    private _subParticipantFieldIds?: string[];
    public get subParticipantFieldIds(): string[] {
        return this._subParticipantFieldIds;
    }

    @Input({
        required: true,
    })
    public set subParticipantFieldIds(fieldIds: string[]) {
        this._subParticipantFieldIds = fieldIds;
        const fieldMap = getMapByGetter(
            this.allParticipantFields,
            (field) => field.id
        );
        this.subParticipantFields = this.subParticipantFieldIds.reduce(
            (result, fieldId) =>
                fieldMap[fieldId] ? [...result, fieldMap[fieldId]] : result,
            [] as IField[]
        );
        this.subParticipantEmailField = this.subParticipantFields.find(field => field.name === 'email');
    }

    attributeName = MULTI_REGISTRATION_ATTRIBUTE_NAMES;

    readonly tableRowActionGetter = this.getTableRowActionGetter();

    editedSubParticipantRecord = signal<DraftSubParticipantRecord>({});

    TRANSLATION_MAP = TRANSLATION_MAP;

    getFieldWithParticipantStatus = getFieldWithParticipantStatus;

    getUniqueEmailValidation = getUniqueEmailValidation;

    public get isRequireTicket() {
        return getIsRequireTicket(this.config);
    }

    constructor(
        private sharedSvc: SharedService,
        private domSvc: AppDomService,
        private host: ElementRef<HTMLElement>,
        private ngZone: NgZone,
        private cdr: ChangeDetectorRef,
        public formSvc: MultiRegistrationFormService,
        private validatorSvc: ValidatorService
    ) {}

    ngOnInit(): void {
        this.validatorSvc.setSubParticipantValidatedCallback(
            (fieldId, value, validationResult, element, errorsContainerElementId) => {
                const subParticipantRootEl = getParentRecursively(
                    element,
                    `[${MULTI_REGISTRATION_ATTRIBUTE_NAMES.subParticipantIdAttrName}]`
                );
                const isMainParticipantAssignedField =
                    fieldId ===
                    MULTI_REGISTRATION_ATTRIBUTE_NAMES.main_participant_assigned_field_id;
                const subParticipantId = isMainParticipantAssignedField
                    ? this.state.main_participant.id
                    : subParticipantRootEl?.getAttribute(
                          MULTI_REGISTRATION_ATTRIBUTE_NAMES.subParticipantIdAttrName
                      );
                if (isMainParticipantAssignedField) {
                    if (!validationResult.value) {
                        this.controller.setMainParticipantTicket(null);
                    }
                    this.editedSubParticipantRecord.update((record) => {
                        record[subParticipantId] = validationResult.value
                            ? this.sharedSvc.clone(this.state.main_participant)
                            : undefined;
                        record[
                            MULTI_REGISTRATION_ATTRIBUTE_NAMES.draftParticipantId
                        ] = undefined;
                        return record;
                    });
                } else {
                    this.editedSubParticipantRecord.update((record) => {
                        record[subParticipantId].participant_attributes[
                            fieldId
                        ] = validationResult.value;
                        return record;
                    });
                }
                const subParticipantFormContainerEl = this.getSubParticipantFormContainerEl(subParticipantId);
                if (subParticipantFormContainerEl) {
                    this.domSvc.setValidationResultToInputElement(
                        fieldId,
                        element,
                        validationResult,
                        this.subParticipantFields,
                        errorsContainerElementId,
                        subParticipantFormContainerEl,
                    );
                }
                this.cdr.detectChanges();
            }
        );
        this.validatorSvc.setMultiRegistrationTicketFieldValidatedCallback(
            (fieldId, validationResult, element) => {
                const subParticipantRootEl = getParentRecursively(
                    element,
                    `:not(.multi-registration__main-participant)[${this.attributeName.subParticipantIdAttrName}]`
                );
                const subParticipantId = subParticipantRootEl?.getAttribute(
                    this.attributeName.subParticipantIdAttrName
                );
                const ticketValue = validationResult.value
                    ? validationResult.value === 'null'
                        ? null
                        : validationResult.value
                    : null;
                if (subParticipantId) {
                    this.editedSubParticipantRecord.update((record) => {
                        record[subParticipantId].ticket_type_id = ticketValue;
                        return record;
                    });
                } else {
                    this.editedSubParticipantRecord.update((record) => {
                        record[this.state.main_participant.id].ticket_type_id =
                            ticketValue;
                        return record;
                    });
                }
                this.cdr.detectChanges();
            }
        );
    }

    async ngAfterViewInit() {
        await this.fillAllInputFieldElements();
    }

    private getTableRowActionGetter(): IRowActionGetter<TableRowObject> {
        return () => ({
            otherActions: [
                {
                    actionFn: (row) =>
                        this.toggleEditSubParticipant(row.id, true),
                    label: TRANSLATION_MAP.EDIT,
                    key: 'edit',
                    disabledFn: (row) => {
                        const ui_participant = row.object.ui_participant;
                        return (!this.isRequireTicket && ui_participant?.is_main) && !getFieldEditableByStatus(ui_participant.status);
                    },
                },
                {
                    actionFn: (row) => {
                        if (row.is_main) {
                            this.controller.setMainParticipantTicket(null);
                        } else {
                            this.controller.removeParticipant(row.id);
                        }
                        this.toggleEditSubParticipant(row.id, false);
                    },
                    label: TRANSLATION_MAP.REMOVE,
                    key: 'remove',
                    additionalClassNames: 'aza-text-error',
                    disabledFn: (row) => {
                        const ui_participant = row.object.ui_participant;
                        return ui_participant.status !== UIParticipantStatus.PENDING_ASSIGNMENT;
                    },
                },
                {
                    actionFn: (row) => {
                        if (row.is_main) {
                            this.controller.setMainParticipantTicket(null);
                        } else {
                            this.controller.setSubParticipantTicket(row.id, null);
                        }
                        this.toggleEditSubParticipant(row.id, false);
                    },
                    label: TRANSLATION_MAP.CANCEL,
                    key: 'cancel',
                    additionalClassNames: 'aza-text-error',
                    disabledFn: (row) => {
                        const ui_participant = row.object.ui_participant;
                        return ![UIParticipantStatus.PENDING_CONFIRMATION, UIParticipantStatus.CONFIRMED].includes(ui_participant.status);
                    },
                },
            ],
        });
    }

    saveDraftAfterDelay = async (draftParticipantId: string, delayMs: number = 0) => {
        await delay(delayMs);
        this.saveDraft(draftParticipantId);
    };

    saveDraft = (draftParticipantId: string) => {
        const draftParticipant =
            this.editedSubParticipantRecord()[draftParticipantId];
        const virtualTicketForNonTicketRegistration = draftParticipant.ticket_options.find(ticket => !!ticket.value);
        const ticketId = this.isRequireTicket ? draftParticipant.ticket_type_id : virtualTicketForNonTicketRegistration.value;
        if (draftParticipantId === this.attributeName.draftParticipantId) {
            const newParticipantId = this.controller.addParticipant(
                draftParticipant.participant_attributes
            );
            if (ticketId) {
                this.controller.setSubParticipantTicket(
                    newParticipantId,
                    ticketId
                );
            }
        } else {
            this.controller.updateParticipant(
                draftParticipantId,
                draftParticipant.participant_attributes
            );
            if (ticketId) {
                if (draftParticipant.is_main) {
                    this.controller.setMainParticipantTicket(
                        ticketId
                    );
                } else {
                    this.controller.setSubParticipantTicket(
                        draftParticipantId,
                        ticketId
                    );
                }
            }
        }

        this.toggleEditSubParticipant(draftParticipantId, false);
    };

    cancelDraft(draftParticipantId: string) {
        const { main_participant } = this.state;
        this.toggleEditSubParticipant(draftParticipantId, false);
        this.recallFillElementOnStatusChanges(
            main_participant.status,
            main_participant,
            this.host.nativeElement.querySelector(
                `.${this.attributeName.assignMainParticipantClassName}`
            )
        );
    }

    private async fillAllInputFieldElements() {
        await firstValueFrom(this.ngZone.onStable);
        this.state.subparticipants.forEach((subParticipant) => {
            this.fillSubParticipantField(subParticipant);
        });
        this.fillSubParticipantField(this.state.main_participant);
    }

    private fillSubParticipantField(
        subParticipant: UIParticipant,
        subParticipantEl?: HTMLElement
    ) {
        subParticipantEl ||= this.getSubParticipantFormContainerEl(subParticipant.id);
        if (subParticipant) {
            const subParticipantWithTicket = {
                id: subParticipant.id,
                ...subParticipant.participant_attributes,
                [this.attributeName.ticket_type_id]:
                    subParticipant.ticket_type_id,
            };
            const subParticipantFieldsWithTicketField =
                this.subParticipantFields.concat([
                    this.formSvc.createTicketField(subParticipant),
                ]);
            if (subParticipant.is_main) {
                const assignMainParticipantFieldData =
                    this.formSvc.createAssignMainParticipantFieldData(
                        this.state
                    );
                subParticipantFieldsWithTicketField.push(
                    assignMainParticipantFieldData.field
                );
                Object.assign(
                    subParticipantWithTicket,
                    assignMainParticipantFieldData.data.participant_attributes
                );
            }
            if (subParticipantEl) {
                this.domSvc.fillInputElements(
                    subParticipantWithTicket,
                    subParticipantFieldsWithTicketField,
                    subParticipantEl
                );
                this.domSvc.setLockedFields(
                    subParticipantFieldsWithTicketField,
                    subParticipantWithTicket,
                    subParticipantEl
                );
                this.domSvc.executeFnOnInputElements(
                    subParticipantWithTicket,
                    subParticipantFieldsWithTicketField,
                    ({inputEls, field}) => {
                        const value = subParticipantWithTicket[field.id];
                        inputEls.forEach(inputEl => this.validatorSvc.setRequiredClass(inputEl, value));
                    },
                    subParticipantEl,
                );
                this.cdr.detectChanges();
            }
        }
    }

    private getSubParticipantFormContainerEl(subParticipantId: string): HTMLElement {
        return Array.from(
            this.host.nativeElement.querySelectorAll<HTMLElement>(
                `.${this.attributeName.classNamePrefixSubParticipant}${subParticipantId}`
            )
        ).find((el) => el.getBoundingClientRect().width > 0);
    }

    showDraftSubParticipant() {
        const { main_participant } = this.state;
        this.editedSubParticipantRecord.update((record) => {
            record[this.attributeName.draftParticipantId] = {
                ticket_options: this.state.ticket_options,
                participant_attributes: {},
                ticket_type_id: null,
                id: 'draftParticipantId',
                editable: true,
            };
            return record;
        });
        this.toggleEditSubParticipant(this.state.main_participant.id, false);
        this.recallFillElementOnStatusChanges(
            main_participant.status,
            main_participant,
            this.host.nativeElement.querySelector(
                `.${this.attributeName.assignMainParticipantClassName}`
            )
        );
    }

    trackByParticipant = (index: number, participant: UIParticipant) => {
        return participant.id;
    };

    recallFillElementOnStatusChanges = (
        status: UIParticipantStatus, // this params is required as it determines the change-detection
        participant: UIParticipant,
        subParticipantEl?: HTMLElement
    ) => {
        (async () => {
            await firstValueFrom(this.ngZone.onStable);
            this.fillSubParticipantField(participant, subParticipantEl);
        })();
    };

    toggleEditSubParticipant(participantId: string, forceValue?: boolean) {
        const currentValue = this.editedSubParticipantRecord()[participantId];
        const newValue = forceValue ?? !currentValue;
        this.editedSubParticipantRecord.update((record) => {
            record[participantId] = newValue
                ? this.sharedSvc.clone(
                      findParticipantById(participantId, this.state)
                  )
                : null;
            return record;
        });
        this.cdr.detectChanges();
    }

    getSaveSubParticipantDisabled = (participantId: string) => {
        const isTicketAssigned = this.isRequireTicket && !this.editedSubParticipantRecord()[participantId]
            ?.ticket_type_id;

        const getErrorElementExist = (): boolean => {
            const selector = `.${GlobalAttributeName.azaInputRequired}, .${GlobalAttributeName.azaFieldValidationError}:not(:empty)`;
            const errorElements = this.getSubParticipantFormContainerEl(participantId)?.querySelectorAll<HTMLElement>(selector) || [];
            return Array.from(errorElements).some(el => el.getBoundingClientRect().width > 0);
        };
        return isTicketAssigned || getErrorElementExist();
    };

    getVirtualTableRowCss = (isTableRow: boolean) => {
        return isTableRow ? 'aza-table-row aza-table-table-view-body-row aza-table-table-view-body-row--cursor-normal' : '';
    };
}
